import { useCallback, useEffect, useState } from "react";
import { fetchSession } from "../api/client.js";
import type { MiniAppSessionResponse } from "../api/types.js";
import { messages } from "../i18n/messages.js";
import { isTelegramWebApp } from "../lib/telegram.js";

export interface SessionState {
  loading: boolean;
  error: string | null;
  session: MiniAppSessionResponse | null;
  retry: () => void;
}

export function useSession(): SessionState {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<MiniAppSessionResponse | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      if (!isTelegramWebApp()) {
        if (!cancelled) {
          setError(messages.session.telegramOnly);
          setLoading(false);
        }
        return;
      }

      try {
        const data = await fetchSession();
        if (!cancelled) {
          setSession(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : messages.session.unavailable);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return { loading, error, session, retry };
}
